import { EDocumentPageStatus } from '@if/core/types'
import {
  CheckCircle as CheckCircleIcon,
  Delete as DeleteIcon,
  DeleteOutline as DeleteOutlineIcon,
  RotateLeft as RotateLeftIcon,
  RotateRight as RotateRightIcon,
} from '@mui/icons-material'
import { alpha, Badge, Button, Checkbox, ImageListItemBar, Stack, Tooltip, Typography, useTheme } from '@mui/material'
import ImageMagnifier from '@src/components/ImageMagnifier'
import { ChangeEventHandler, MouseEventHandler, useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { IconButtonWithTooltip } from '../../../components'
import { DocumentPageDisplayInfo } from '../../../data/types/DocumentPageDisplayInfo'

interface Props {
  page: DocumentPageDisplayInfo
  onRotatePage: MouseEventHandler<HTMLButtonElement>
  onDeletePage: ChangeEventHandler<HTMLInputElement>
  getTitle: (page: DocumentPageDisplayInfo) => string
  onImageClicked: MouseEventHandler<HTMLButtonElement>
  showBanners: boolean
  index: number
  selectedPages: string[]
  isDraggable: boolean
  isActionDisabled: boolean
  showZoom?: boolean
  gridWidth?: number
  gridHeight?: number
}

const Thumbnail = ({
  page,
  onRotatePage,
  onDeletePage,
  getTitle,
  onImageClicked,
  showBanners,
  index,
  selectedPages,
  isDraggable,
  isActionDisabled,
  showZoom = false,
  gridWidth,
  gridHeight,
}: Props) => {
  const { t } = useTranslation()
  const theme = useTheme()

  const [scale, setScale] = useState<number>(0)
  const [isVisible, setIsVisible] = useState(false)
  const divRef = useRef<HTMLDivElement>(null)

  const thumbnailSrc = isVisible && page.src ? page.src : undefined

  const scaleImage = useCallback(() => {
    let scale = 1

    if ((page.rotation === 90 || page.rotation === 270) && gridHeight && gridWidth && divRef.current) {
      if (gridHeight < divRef.current.clientWidth) {
        scale = gridHeight / divRef.current.clientWidth
      } else if (gridWidth < divRef.current.clientHeight) {
        scale = gridWidth / divRef.current.clientHeight
      } else {
        scale = Math.min(divRef.current.clientHeight / gridWidth, divRef.current.clientWidth / gridHeight)
      }
    }
    setScale(scale)
  }, [page, gridHeight, gridWidth])

  useEffect(() => {
    if (gridHeight && gridWidth) {
      scaleImage()
    }
  }, [gridWidth, gridHeight, scaleImage])

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true)
          observer.disconnect()
        }
      },
      { rootMargin: '200px' },
    )
    if (divRef.current) observer.observe(divRef.current)
    return () => observer.disconnect()
  }, [])

  const handleImageLoad = useCallback(() => {
    scaleImage()
  }, [scaleImage])

  return (
    <div ref={divRef} style={{
      objectFit: 'contain',
      transform: `rotate(${page.rotation}deg) scale(${page.rotation === 90 || page.rotation === 270 ? scale : 1})`,
      transformOrigin: 'center center',
      width: '100%',
      height: '100%',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center'
    }}>
      {isDraggable && (
        <ImageMagnifier
          showZoom={showZoom}
          src={thumbnailSrc}
          width='100%'
          height='100%'
          alt="Loading"
          objectFit='contain'
          degrees={page.rotation}
          scale={scale}
          scaleImage={handleImageLoad}
        />
      )}
      {!isDraggable && (
        <Button
          sx={{
            width: '100%',
            height: '100%',
            overflow: 'auto',
            cursor: 'pointer',
            p: 0,
            '& > *': {
              pointerEvents: 'auto',
            },
          }}
          id={page.id}
          disabled={isActionDisabled}
          onClick={onImageClicked}
        >          
          <ImageMagnifier
            showZoom={showZoom}
            src={thumbnailSrc}
            width='100%'
            height='100%'
            alt="Loading"
            objectFit='contain'
            degrees={page.rotation}
            scale={scale}
            scaleImage={handleImageLoad}
          />
        </Button>
      )}
      {showBanners && (
        <div>
          <ImageListItemBar
            title={
              <Stack direction="row" alignItems="center" justifyContent="space-between">
                <IconButtonWithTooltip
                  tooltip={t('document.rotateLeft')}
                  icon={<RotateLeftIcon sx={{ color: 'rgba(255, 255, 255, 0.54)' }} />}
                  aria-label="rotateLeft"
                  aria-rowindex={index}
                  aria-valuenow={page.rotation}
                  onClick={onRotatePage}
                  disabled={isActionDisabled}
                />

                <Checkbox
                  checked={page.pageStatus === EDocumentPageStatus.Deleted}
                  color="error"
                  icon={<DeleteOutlineIcon />}
                  checkedIcon={<DeleteIcon />}
                  value={index}
                  name={page.pageStatus}
                  onChange={onDeletePage}
                  disabled={isActionDisabled}
                />

                <IconButtonWithTooltip
                  tooltip={t('document.rotateRight')}
                  icon={<RotateRightIcon sx={{ color: 'rgba(255, 255, 255, 0.54)' }} />}
                  aria-label="rotateRight"
                  aria-rowindex={index}
                  aria-valuenow={page.rotation}
                  onClick={onRotatePage}
                  disabled={isActionDisabled}
                />

                <Typography variant="body2" position="absolute" right={0} marginRight="0.5rem" marginTop="2.5rem">
                  {`${page.pageNumber}`}
                </Typography>
              </Stack>
            }
          />
          <ImageListItemBar
            sx={[
              {
                cursor: 'pointer',
              },
              page.pageStatus === EDocumentPageStatus.Deleted
                ? {
                    backgroundColor: alpha(theme.palette.error.main, 0.5),
                  }
                : {
                    backgroundColor: '',
                  },
            ]}
            title={
              <Tooltip title={<span>{getTitle(page)}</span>} arrow placement="top">
                <span> {getTitle(page)}</span>
              </Tooltip>
            }
            position="top"
            actionIcon={
              <Stack direction="row" justifyContent="center">
                <div>
                  {page.pageStatus === EDocumentPageStatus.New && (
                    <Badge color="primary" badgeContent="NEW" sx={{ mr: 3 }} />
                  )}
                </div>

                <div>
                  {selectedPages.findIndex((x) => x === page.id) !== -1 && (
                    <Badge
                      color="primary"
                      badgeContent={selectedPages.findIndex((x) => x === page.id) + 1}
                      sx={{ mr: 2 }}
                    >
                      <CheckCircleIcon color="primary" />
                    </Badge>
                  )}
                </div>
              </Stack>
            }
          />
        </div>
      )}
    </div>
  )
}

export default Thumbnail
